import axios, { type AxiosResponse } from 'axios';
import { CHUNKED_UPLOAD_COMPLETE, CHUNKED_UPLOAD_PATH } from 'src/constants';

// Determine chunk size: use VITE_CHUNK_SIZE when provided, otherwise default to 10MB
const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const envChunk = parseInt(import.meta.env.VITE_CHUNK_SIZE || '', 10);
const chunkSize = Number.isFinite(envChunk) && envChunk > 0 ? envChunk : DEFAULT_CHUNK_SIZE;
const upload_url = window.location.origin + CHUNKED_UPLOAD_PATH;
const finish_url = window.location.origin + CHUNKED_UPLOAD_COMPLETE;

interface NativeField {
  slug: string;
  content: string;
}

interface FinalizeData {
  asset_id: string;
  asset: {
    name: string;
    media_group?: string;
    formats?: Array<{
      id: string;
      fmtType?: { name: string };
      format_type?: string;
      mediapath: string;
      filename: string;
      mimetype: string;
    }>;
  };
}

interface HandleFileUploadOptions {
  mediaGroupName?: string;
  setIsFinalizing?: (flag: boolean) => void;
  nativeFields?: NativeField[];
  onFinalize?: (data: FinalizeData | null) => void;
}

interface FileInputEvent {
  target: {
    files: FileList | File[] | null;
  };
}

function handleFileUpload(
  e: FileInputEvent,
  setProgress: (value: number) => void,
  setStateError: (msg: string | null) => void,
  setFileName: (name: string) => void,
  options: HandleFileUploadOptions = {},
): void {
  const { mediaGroupName, setIsFinalizing, nativeFields = [], onFinalize } = options;
  let chunk_index = 0;
  let num_chunks = 0;
  let upload_id: string | number = 0;
  let attempts = 0;
  let error_ref: string | null = null;
  let file: File | null = null;

  const sleep = (ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const get_cur_chunk = (): Blob => {
    if (!file) throw new Error('File is not initialized');
    const start = chunk_index * chunkSize;
    const remaining = file.size - start;
    const end = remaining <= chunkSize ? remaining : chunkSize;

    return file.slice(start, start + end, file.type);
  };

  const set_cur_chunk = (offset: number): void => {
    chunk_index = Math.ceil(offset / chunkSize);
  };

  const set_chunk_meta = (): void => {
    if (!file) return;
    const total_size = file.size;

    if (total_size <= 0) {
      num_chunks = 0;
      return;
    }

    num_chunks = Math.floor(total_size / chunkSize);
    if (total_size % chunkSize > 0) {
      num_chunks++;
    }
  };

  const finish_upload = (): Promise<void> => {
    const data = new FormData();
    data.append('upload_id', String(upload_id));
    if (mediaGroupName) {
      data.append('media_group_name', mediaGroupName);
    }
    if (Array.isArray(nativeFields) && nativeFields.length > 0) {
      try {
        data.append('native_fields', JSON.stringify(nativeFields));
      } catch (_err) {
        // ignore serialization error; backend will proceed without native_fields
      }
    }
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    const beforeUnload = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      event.returnValue = 'Upload is finalizing. Please wait to avoid corrupting the upload.';
      return event.returnValue;
    };

    if (typeof setIsFinalizing === 'function') setIsFinalizing(true);
    window.addEventListener('beforeunload', beforeUnload);

    return axios({
      method: 'post',
      url: finish_url,
      headers: headers,
      data: data,
    })
      .then((response: AxiosResponse<FinalizeData>) => {
        setProgress(100);
        if (typeof setStateError === 'function') setStateError(null);
        if (typeof onFinalize === 'function') {
          try {
            onFinalize(response?.data || null);
          } catch (_) {
            // ignore UI callback errors
          }
        }
      })
      .catch((error) => {
        if (error.response?.data?.detail) {
          error_ref = error.response.data.detail;
          setStateError(error_ref);
        }
      })
      .finally(() => {
        window.removeEventListener('beforeunload', beforeUnload);
        if (typeof setIsFinalizing === 'function') setIsFinalizing(false);
      });
  };

  const continue_upload = (): Promise<void> | undefined => {
    if (!file) return;

    const data = new FormData();
    const offset = chunk_index * chunkSize;
    data.append('the_file', get_cur_chunk());
    data.append('upload_id', String(upload_id));

    const remaining = file.size - offset;
    const end = remaining < chunkSize ? remaining : chunkSize;
    const headers = {
      'Content-Type': 'multipart/form-data',
      'Content-Range': `bytes ${offset}-${offset + end - 1}/${file.size}`,
    };

    if (chunk_index >= num_chunks) {
      return;
    }

    return axios({
      method: 'post',
      url: upload_url,
      headers: headers,
      data: data,
    })
      .then(() => {
        chunk_index += 1;
        attempts = 0;
        setProgress(Math.round((chunk_index * 100) / num_chunks));
        if (typeof setStateError === 'function') setStateError(null);
        return sleep(100);
      })
      .catch((error) => {
        console.log(error.response);
        if (error.response?.data?.offset) {
          set_cur_chunk(error.response.data.offset);
        }

        if (attempts < 12) {
          attempts++;
          if (attempts === 1) {
            return sleep(5000);
          }

          if (attempts === 2) {
            return sleep(10000);
          }

          if (attempts === 3) {
            return sleep(30000);
          }

          if (attempts > 3) {
            return sleep(60000);
          }
        } else {
          error_ref = 'Error uploading media';
          setStateError(error_ref);
        }
      });
  };

  const initial_upload = (): Promise<void> => {
    if (!file) return Promise.resolve();

    const data = new FormData();
    data.append('the_file', get_cur_chunk());
    data.append('filename', file.name);
    data.append('chunk_size', String(chunkSize));

    let headers: Record<string, string>;
    if (file.size < chunkSize) {
      headers = {
        'Content-Type': 'multipart/form-data',
      };
    } else {
      headers = {
        'Content-Type': 'multipart/form-data',
        'Content-Range': `bytes 0-${chunkSize - 1}/${file.size}`,
      };
    }
    return axios({
      method: 'post',
      url: upload_url,
      headers: headers,
      data: data,
    })
      .then((response) => {
        chunk_index += 1;
        upload_id = response.data.upload_id;
        if (typeof setStateError === 'function') setStateError(null);
      })
      .catch((error) => {
        console.log(error.response);
        if (error.response?.data?.offset) {
          set_cur_chunk(error.response.data.offset);
          upload_id = error.response.data.upload_id;
        } else if (error.response?.data?.detail) {
          error_ref = error.response.data.detail;
          setStateError(error_ref);
        } else {
          error_ref = 'There was a problem initiating the upload';
          setStateError(error_ref);
        }
      });
  };

  async function entry(): Promise<void> {
    if (!e.target.files || e.target.files.length === 0) return;
    file = e.target.files[0] as File;
    setFileName(file.name);
    if (typeof setStateError === 'function') setStateError(null);

    set_chunk_meta();

    await initial_upload();

    if (num_chunks > 1 && error_ref == null) {
      while (chunk_index < num_chunks && error_ref === null) {
        await continue_upload();
      }
    }

    if (error_ref == null) {
      await finish_upload();
    }
  }

  entry();
}

export default handleFileUpload;
